import { normalizeWordText } from '../lib/normalize-word-text';
import { TranscriptionBlockData } from '../lib/transcription.types';

/** Raw query shorter than this is ignored (no filter, no highlights). */
export const TRANSCRIPTION_SEARCH_MIN_QUERY_LENGTH = 3;

/**
 * Normalized query used for matching, or empty string when the trimmed input is too short.
 */
export function effectiveNormalizedSearchQuery(trimmedQuery: string): string {
  if (trimmedQuery.length < TRANSCRIPTION_SEARCH_MIN_QUERY_LENGTH) {
    return '';
  }

  return normalizeWordText(trimmedQuery);
}

export type TranscriptionSearchMatch = {
  wordId: string;
};

/**
 * Returns ordered word ids whose normalized text contains the normalized query.
 * Skips music/non-speech blocks. Empty query yields no matches.
 */
export function buildTranscriptionSearchMatches(
  blocks: TranscriptionBlockData[],
  normalizedQuery: string,
): TranscriptionSearchMatch[] {
  if (!normalizedQuery) {
    return [];
  }

  const matches: TranscriptionSearchMatch[] = [];

  for (const block of blocks) {
    if (block.isMusic) {
      continue;
    }

    for (const word of block.words) {
      if (word.normalizedText.includes(normalizedQuery)) {
        matches.push({ wordId: word.id });
      }
    }
  }

  return matches;
}

/**
 * When `normalizedQuery` is empty, returns all blocks. Otherwise returns only blocks that contain
 * at least one word whose normalized text includes the query (music segments are omitted).
 */
export function filterTranscriptionBlocksBySearchQuery(
  blocks: TranscriptionBlockData[],
  normalizedQuery: string,
): TranscriptionBlockData[] {
  if (!normalizedQuery) {
    return blocks;
  }

  return blocks.filter((block) => {
    if (block.isMusic) {
      return false;
    }
    return block.words.some((word) => word.normalizedText.includes(normalizedQuery));
  });
}
